import { useContext, useMemo } from 'react';

import dayjs from 'dayjs';
import { DateRange } from '@cast/shared-types';
import { DateRangeFilter } from '@cast/shared-date-ui';

import { useReliabilityData } from 'features/cluster/_components/reliability/_hooks/useReliabilityData';

import { useRightsizingTimeRangeSelection } from '../../../../_hooks/useRightsizingTimeRangeSelection';
import { TimeRangeSelection } from '../../../../_hooks/useCurrentWorkloadMetrics';
import { WorkloadDetailsContext } from '../../../_context';

export const useWorkloadReliabilityData = ({
  timeRangeSelection,
}: {
  timeRangeSelection?: TimeRangeSelection;
} = {}) => {
  const { workload } = useContext(WorkloadDetailsContext);
  const rightsizingTimeRangeSelection = useRightsizingTimeRangeSelection();
  const { getRange, timeZone, preset } =
    timeRangeSelection ?? rightsizingTimeRangeSelection;

  const range = useMemo(() => getRange(), [getRange]);
  const from = useMemo(() => dayjs.utc(range.from), [range.from]);
  const to = useMemo(() => dayjs.utc(range.to), [range.to]);
  const granularDateRange = useMemo(() => ({ from, to }), [from, to]);
  const currentDateRange = useMemo<DateRange>(() => [from, to], [from, to]);

  const reliabilityData = useReliabilityData({
    namespace: workload?.namespace,
    workloadType: workload?.kind,
    workloadName: workload?.name,
    granularDateRange,
    timezone: timeZone,
    currentDateRange,
    currentDateRangeFilter: preset as DateRangeFilter | undefined,
    includeTable: false,
    includePreviousPeriod: false,
  });

  return { ...reliabilityData, timeZone };
};
